<template>
  <section class="simulations-list">
    <simulations-list-filters class="simulations-list-filters" />

    <ul class="simulations-list-items">
      <template v-for="group in grouped">
        <li
          :key="`${group.key}-item`"
          class="simulations-list-item"
        >
          <router-link
            class="simulations-list-link"
            :to="link(group.simulations[0])"
          >
            <typography-heading
              hierarchy="h3"
              icon="chevron-right"
            >
              {{ group.simulations[0].lead.name | namelize }}

              <template v-slot:subtitle>
                #{{ group.simulations[0].proposal_simulation.id }}
                - cadastrada dia {{ group.simulations[0].created_at | createdAt }}

                <context-tag
                  v-if="tagged(group.simulations[0])"
                  color="external"
                  class="simulations-list-online"
                  :icon="['fas', 'right-to-bracket']"
                >
                  Via {{ tagged(group.simulations[0]) }}
                </context-tag>
              </template>
            </typography-heading>

            <information-line
              :label="vehicle(group)"
            >
              <span class="simulations-list-vehicle-price">
                {{ price(group) }}
              </span>
            </information-line>

            <information-line
              :label="seller(group)"
              :value="store(group)"
              :copiable="false"
              class="simulations-list-seller"
            />
          </router-link>

          <router-link
            v-for="simulation in group.simulations"
            :key="simulation.uuid"
            :to="link(simulation)"
            class="simulations-list-sublink"
          >
            <information-line
              class="simulations-list-subsimulation simulations-list--subsimulation-vertical"
              :copiable="false"
            >
              <template v-slot:label>
                <context-tag
                  class="simulation-list-unread"
                  color="alert-danger"
                  :disabled="simulation.proposal_simulation.viewed"
                  size="small"
                >
                  <span class="simulation-list-unread--placeholder">não lida</span>
                  {{ simulation.proposal_simulation.viewed ? 'lida' : 'não lida' }}
                </context-tag>

                Valor da entrada: {{ downPayment(simulation) }}
              </template>

              <context-tag :color="statusColor(simulation)">
                {{ status(simulation) }}
              </context-tag>
            </information-line>
          </router-link>
        </li>

        <span
          :key="`${group.key}-separator`"
          class="simulations-list-separator"
        />
      </template>

      <context-state
        :state="currentState"
        tag="li"
      >
        <template v-slot:loading>
          Carregando simulações...
        </template>

        <template v-slot:notfound>
          Nenhuma simulação encontrada :(
        </template>

        <template v-slot:empty>
          Sem mais simulações
        </template>
      </context-state>
    </ul>
  </section>
</template>

<script>
  import { mapState } from 'vuex';
  import mobile from '@sdk/helpers/interface/mobile';
  import origin from '@sdk/helpers/interface/origin';

  // eslint-disable-next-line import/no-extraneous-dependencies
  import sort from '@/_CRUDL/helpers/data/sort';

  // eslint-disable-next-line import/no-extraneous-dependencies
  import preload from '@/helpers/mixins/preload';

  import namelize from '@sdk/helpers/formatters/namelize';
  import monetize from '@sdk/helpers/formatters/monetize';
  import InformationLine from '@cordel/components/Information/Line.vue';
  import TypographyHeading from '@cordel/components/Typography/Heading.vue';

  import ContextState from '@cordel/components/Context/State.vue';
  import ContextTag from '@cordel/components/Context/Tag.vue';

  import SimulationsListFilter from '@sdk/views/Simulations/List/Filter.vue';
  import SimulationsListFilters from '@sdk/views/Simulations/List/Filters.vue';

  import normalizeConditions from './Read/normalizer';
  import { IDENTIFIERS, FAILED, title } from './Read/status';

  export default {
    name: 'SimulationsList',
    components: {
      ContextState,
      ContextTag,
      InformationLine,
      TypographyHeading,

      SimulationsListFilters,
    },
    mixins: [
      preload,
      mobile,
    ],
    props: {
      scrollable: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        nav: {
          action: SimulationsListFilter,
        },
      };
    },
    computed: {
      ...mapState([
        'simulations',
        'stores',
      ]),

      filters() {
        return this.simulations.list.filters;
      },
      list() {
        return sort(this.simulations.list.items, this.filters.sort);
      },
      loading() {
        return this.simulations.list.loading || this.stores.list.loading;
      },
      currentState() {
        if (this.loading) {
          return 'loading';
        }

        if (this.list.length === 0) {
          return 'notfound';
        }

        if (this.scrollable) {
          return 'empty';
        }

        return null;
      },

      grouped() {
        return this.list.reduce((reduced, simulation) => {
          // eslint-disable-next-line no-underscore-dangle
          const key = `${simulation.lead.id}-${simulation.vehicle.vehicle_model.id}-${simulation.vehicle.asset_value}-${simulation._CRUDL_PAGE}-${this.tagged(simulation)}`;

          if (reduced[key]) {
            reduced[key].simulations.push(simulation);
          } else {
            reduced[key] = {
              key,
              simulations: [simulation],
            };
          }

          return reduced;
        }, {});
      },
    },

    beforeMount() {
      this.$store.dispatch('simulations/clear', { action: 'list' });
    },

    methods: {
      vehicle(group) {
        const simulation = group.simulations[0];

        if (simulation.vehicle && simulation.vehicle.vehicle_model) {
          return `${simulation.vehicle.vehicle_model.brand} ${simulation.vehicle.vehicle_model.model_name} - ${simulation.vehicle.vehicle_model.version}`;
        }

        return 'Veículo desconhecido';
      },
      price(group) {
        return monetize(group.simulations[0].vehicle.asset_value);
      },
      seller(group) {
        return `${namelize(group.simulations[0].proposal_simulation.seller.name, 2)}`;
      },
      store(group) {
        return `${group.simulations[0].proposal_simulation.store.name}`;
      },

      status(simulation) {
        let pre = this.statusNumber(simulation);

        const {
          pre_approval_status: status,
        } = this.simulations.list.filters;

        if (status.length) {
          pre = parseInt(status[0], 10);
        }

        switch (pre) {
          case 1:
          case 2:
          case 3:
            return title(pre);
          default:
            return title(IDENTIFIERS[FAILED]);
        }
      },
      statusNumber(simulation) {
        const conditions = normalizeConditions(simulation.conditions);

        const max = Math.max.apply(
          null,
          conditions.fixed.concat(conditions.variable)
            .map(i => (i.key === IDENTIFIERS[FAILED] ? -1 : i.key)),
        );

        return max;
      },
      statusColor(simulation) {
        let pre = this.statusNumber(simulation);

        const {
          pre_approval_status: status,
        } = this.simulations.list.filters;

        if (status.length) {
          pre = parseInt(status[0], 10);
        }

        switch (pre) {
          case 1:
            return 'status-03';
          case 2:
            return 'status-01';
          case 3:
            return 'status-04';
          default:
            return 'status-02';
        }
      },

      link(simulation) {
        return {
          name: 'simulations-read',
          params: {
            uuid: simulation.proposal_simulation.id,
          },
          query: {
            store_id: simulation.proposal_simulation.store.id,
          },
        };
      },
      first(group) {
        this.$router.push(this.link(group.simulations[0]));
      },

      tagged(simulation) {
        return origin(simulation.proposal_simulation) || origin(simulation);
      },

      downPayment(simulation) {
        const first = simulation.conditions.find(i => !i.suggestion_reason);

        return monetize(first ? first.down_payment : 0);
      },
    },
  };
</script>

<style lang="scss">
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .simulations-list-filters {
    .cordel-section-item {
      margin-top: var(--cordel-spacing-s);

      &:nth-child(1),
      &:nth-child(2) {
        margin-top: 0;
      }

      @media all and (max-width: $cordel-breakpoint-mobile) {
        &:nth-child(2) {
          margin-top: var(--cordel-spacing-s);
        }
      }
    }
  }

  .simulations-list-subsimulation {
    .cordel-information-line-value {
      @media all and (max-width: $cordel-breakpoint-mobile) {
        margin-left: 0;
        margin-top: var(--cordel-spacing-mini);
      }
    }
  }
</style>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .simulations-list-items {
    list-style: none;
    margin-top: var(--cordel-spacing-xl);
    overflow: hidden;
  }

  .simulations-list-item {
    margin: var(--cordel-spacing-xl) 0;
  }

  .simulations-list-separator {
    background-color: var(--cordel-color-neutral-dark);
    display: block;
    height: var(--cordel-border-width-soft);
    margin: 0 var(--product--pwa--horizontal);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      margin: 0 var(--product--pwa--horizontal);
    }
  }

  .simulations-list-link {
    display: block;
    padding: 0 var(--product--pwa--horizontal) var(--cordel-spacing-nano);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: 0 var(--product--pwa--horizontal) var(--cordel-spacing-nano);
    }
  }

  .simulations-list-sublink {
    display: block;
    padding: var(--cordel-spacing-mini) var(--product--pwa--horizontal);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: var(--cordel-spacing-micro) var(--product--pwa--horizontal);
    }

    .simulations-list-link:hover + &,
    &:hover {
      background: var(--cordel-color-neutral-lighter);
    }

    &:focus {
      box-shadow: 0 0 0 var(--cordel-border-width-thin) var(--cordel-color-highlight-primary);
      outline: none;
    }
  }

  .simulations-list-seller {
    margin-top: var(--cordel-spacing-mini);
  }

  .simulations-list-online {
    margin-top: var(--cordel-spacing-nano);
    margin-left: var(--cordel-spacing-nano);
  }

  .simulation-list-tag-mobile {
    margin-top: var(--cordel-spacing-xl);
    width: 100%;
  }

  .simulation-list-unread {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
    margin-right: var(--cordel-spacing-nano);
    position: relative;
    top: -0.125em;
  }

  .simulation-list-unread--placeholder {
    display: block;
    height: 0;
    overflow: hidden;
  }

  .simulations-list-subsimulation {
    @media all and (max-width: $cordel-breakpoint-mobile) {
      &.simulations-list--subsimulation-vertical {
        flex-direction: column;
        align-items: flex-start;
      }
    }
  }

  .simulations-list-vehicle-price {
    white-space: nowrap;
  }
</style>
